import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';

/** Viewport-space top-left of one draggable card. */
interface Point {
  x: number;
  y: number;
}

/** Session memory - survives dock/undock remounts, dies with the tab. */
const positions: Record<string, Point | undefined> = {};

/** Gap the card keeps from the viewport edges and from under the top bar. */
const EDGE_MARGIN = 8;
/** Arrow-key step, one 8px grid unit. */
const NUDGE_STEP = 8;
/** Bottom keep-out for a card that sits above the toolbar - the band the toolbar itself fills. */
const DEFAULT_BOTTOM_BAND = '--board-inset-bottom';

const ARROW_STEPS: Record<string, Point | undefined> = {
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
};

/** Resolved px per substituted token stream. The stream changes whenever the metric does. */
const resolvedLengths: Record<string, number | undefined> = {};

/** A custom property computes to an unevaluated token stream, so layout has to do the calc(). */
function resolveLength(raw: string, fallback: number): number {
  // an unsubstituted var() would pin the first pixel value forever, and --device-bottom-inset moves
  const cacheable = raw.indexOf('var(') < 0;
  const hit = cacheable ? resolvedLengths[raw] : undefined;
  if (hit !== undefined) return hit;
  const probe = document.createElement('div');
  probe.style.cssText = 'position:absolute;top:0;left:0;width:0;visibility:hidden;pointer-events:none';
  probe.style.height = raw;
  document.body.appendChild(probe);
  const px = probe.offsetHeight || fallback;
  document.body.removeChild(probe);
  if (cacheable) resolvedLengths[raw] = px;
  return px;
}

// shell metrics live in tokens.css - read them rather than restating the numbers here
function readMetric(name: string, fallback: number): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const value = parseFloat(raw);
  if (!isNaN(value) && raw.indexOf('calc') < 0) return value;
  return raw === '' ? fallback : resolveLength(raw, fallback);
}

function clampAxis(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Keep the whole card on screen, below the top bar and clear of the band it declared. */
function clampToViewport(x: number, y: number, width: number, height: number, bottomBandVar: string): Point {
  const topBar = readMetric('--topbar-height', 55);
  const bottomBand = Math.max(readMetric(bottomBandVar, 0), EDGE_MARGIN);
  const minX = EDGE_MARGIN;
  const minY = topBar + EDGE_MARGIN;
  // Math.max guards short viewports where the two bands would otherwise cross
  const maxX = Math.max(minX, window.innerWidth - width - EDGE_MARGIN);
  const maxY = Math.max(minY, window.innerHeight - height - bottomBand);
  return { x: clampAxis(x, minX, maxX), y: clampAxis(y, minY, maxY) };
}

/** offsetLeft/Top are offsetParent-relative; this is that parent's viewport origin. */
function originOf(node: HTMLElement, rect: DOMRect): Point {
  return { x: rect.left - node.offsetLeft, y: rect.top - node.offsetTop };
}

function moveTo(node: HTMLElement, point: Point, origin: Point): void {
  node.style.left = `${point.x - origin.x}px`;
  node.style.top = `${point.y - origin.y}px`;
}

interface DragState extends Point {
  pointerId: number;
  width: number;
  height: number;
  origin: Point;
}

export interface DraggableChromeOptions {
  /** Session key - two cards must not share one. */
  id: string;
  /** False for the docked/expanded variant, which stays anchored by its own CSS. */
  enabled: boolean;
  /** Custom property naming the bottom keep-out; a card living inside the default band must name a smaller one. */
  bottomBandVar?: string;
  /** Changing it re-clamps the saved spot, e.g. a folded bar growing back into the full card. */
  refit?: string | number | boolean;
}

export interface DraggableChrome {
  ref: (node: HTMLElement | null) => void;
  dragging: boolean;
  handleProps: {
    onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
    onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void;
  };
}

/**
 * Drag a floating chrome card by its handle, with pointer events so touch works.
 * Position is written straight to node.style, never rendered, so React never fights it.
 */
export function useDraggableChrome({ id, enabled, bottomBandVar = DEFAULT_BOTTOM_BAND, refit }: DraggableChromeOptions): DraggableChrome {
  const nodeRef = useRef<HTMLElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const [dragging, setDragging] = useState(false);

  const ref = useCallback((node: HTMLElement | null) => {
    nodeRef.current = node;
  }, []);

  // re-apply the remembered position, clamped to whatever the viewport is now
  const place = useCallback(() => {
    const node = nodeRef.current;
    const saved = positions[id];
    if (!node || !saved) return;
    const rect = node.getBoundingClientRect();
    const next = clampToViewport(saved.x, saved.y, rect.width, rect.height, bottomBandVar);
    positions[id] = next;
    moveTo(node, next, originOf(node, rect));
  }, [id, bottomBandVar]);

  useLayoutEffect(() => {
    const node = nodeRef.current;
    if (!node) return;
    if (!enabled) {
      // the docked variant must sit exactly where its own CSS puts it
      node.style.left = '';
      node.style.top = '';
      return;
    }
    place();
  }, [enabled, place, refit]);

  useEffect(() => {
    if (!enabled) return undefined;
    // a smaller window must never strand the card off-screen
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [enabled, place]);

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const node = nodeRef.current;
    if (!node || !enabled || event.button !== 0) return;
    const rect = node.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      origin: originOf(node, rect),
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
  }, [enabled]);

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    const node = nodeRef.current;
    if (!drag || !node || event.pointerId !== drag.pointerId) return;
    const next = clampToViewport(event.clientX - drag.x, event.clientY - drag.y, drag.width, drag.height, bottomBandVar);
    positions[id] = next;
    moveTo(node, next, drag.origin);
  }, [id, bottomBandVar]);

  const endDrag = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    dragRef.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const onKeyDown = useCallback((event: ReactKeyboardEvent<HTMLElement>) => {
    const node = nodeRef.current;
    const step = ARROW_STEPS[event.key];
    if (!node || !enabled || !step) return;
    event.preventDefault();
    const rect = node.getBoundingClientRect();
    const next = clampToViewport(
      rect.left + step.x * NUDGE_STEP,
      rect.top + step.y * NUDGE_STEP,
      rect.width,
      rect.height,
      bottomBandVar
    );
    positions[id] = next;
    moveTo(node, next, originOf(node, rect));
  }, [enabled, id, bottomBandVar]);

  return {
    ref,
    dragging,
    handleProps: { onPointerDown, onPointerMove, onPointerUp: endDrag, onPointerCancel: endDrag, onKeyDown },
  };
}
